import { singletonRefresh, isRefreshInProgress } from './tokenRefresh';
import { getCmsHosts } from './cmsSites';
import { isPublicPath } from './publicPaths';

function resolveApiBase(): string {
  const envBase = process.env.NEXT_PUBLIC_API_BASE_URL;
  if (typeof window !== 'undefined') {
    const host = window.location.hostname;
    const port = window.location.port;
    // Support portal and public saldo use relative /api/ (proxied by Apache/nginx)
    if (host.startsWith('support.') || host.startsWith('saldo.')) {
      return '';
    }
    // Public CMS websites are served from their own domain and proxy /api to the backend.
    if (getCmsHosts().includes(host.toLowerCase())) {
      return '';
    }
    // Dev server on nginx port 4443 — API proxied by nginx same-origin
    if (port === '4443') {
      return '';
    }
    if (host !== 'localhost' && host !== '127.0.0.1') {
      return `https://api.${host}`;
    }
  }
  return envBase || 'http://localhost:8082';
}

export { resolveApiBase };

export const API_BASE = resolveApiBase();

export class ApiError extends Error {
  status: number;
  remainingAttempts?: number;
  retryAfterSeconds?: number;

  constructor(status: number, message: string, extra?: { remainingAttempts?: number; retryAfterSeconds?: number }) {
    super(message);
    this.status = status;
    this.name = 'ApiError';
    if (extra) {
      this.remainingAttempts = extra.remainingAttempts;
      this.retryAfterSeconds = extra.retryAfterSeconds;
    }
  }
}

const LOADING_START_EVENT = 'armorik:loading:start';
const LOADING_DONE_EVENT = 'armorik:loading:done';

function notifyLoadingStart() {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(LOADING_START_EVENT));
  }
}

function notifyLoadingDone() {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(LOADING_DONE_EVENT));
  }
}

export async function apiFetch<T>(path: string, init?: RequestInit & { silent?: boolean }): Promise<T> {
  const { headers: extraHeaders, silent, ...rest } = init ?? {};
  // Background GET requests (SWR revalidations, focus/refetch polls) never
  // show the loading bar. Only user-initiated fetches do: a GET shortly
  // after a click, any mutation, or an explicit silent:false.
  const method = ((rest.method as string | undefined) ?? 'GET').toUpperCase();
  const quiet = silent === true || (silent === undefined && method === 'GET'
    && Date.now() - lastUserActionAt >= USER_ACTION_WINDOW_MS);
  // Auth is handled via HttpOnly accessToken cookie (credentials: 'include').
  // No need to read token from localStorage or send Authorization header.
  const makeRequest = async (_authHeader?: Record<string, string>) => {
    const isFormData = rest.body instanceof FormData;
    return fetch(`${resolveApiBase()}${path}`, {
      ...rest,
      credentials: 'include',
      headers: {
        ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
        ...(extraHeaders || {})
      },
    });
  };

  if (!quiet) notifyLoadingStart();
  try {
    let res = await makeRequest();

  if (!res.ok) {
    if (res.status === 401 && typeof window !== 'undefined' && path !== '/api/auth/login' && path !== '/api/auth/refresh') {
      // If a refresh is already in progress (e.g. proactive refresh from auth.tsx),
      // singletonRefresh() will return the same promise — no duplicate request.
      // The 3s setTimeout in tokenRefresh.ts + backend grace period ensure
      // concurrent/near-concurrent refreshes all succeed.
      try {
        // Small optimization: if refresh just completed (within 3s), the access
        // token cookie is already updated. Skip the await and retry directly.
        const refreshPromise = singletonRefresh();
        if (!isRefreshInProgress()) {
          // Refresh already resolved (was in the 3s cooldown window).
          // The access token cookie should already be updated.
        }
        await refreshPromise;
        // Retry the original request — cookie is already set by the refresh response.
        res = await makeRequest();
        if (res.ok) {
          const contentType = res.headers.get('content-type');
          const isJson = !!contentType && contentType.includes('application/json');
          const text = await res.text();
          if (!text) return null as T;
          if (isJson) return JSON.parse(text) as T;
          return {} as T;
        }
        // Retry also failed — fall through to throw below
      } catch (refreshErr) {
        const msg = refreshErr instanceof Error ? refreshErr.message : '';
        const isSessionExpired = msg.includes('Session expired');
        const isBackendUnavailable = msg.includes('Backend unavailable') || msg.includes('Network error') || msg.includes('fetch');

        if (isSessionExpired) {
          // 401 = genuinely invalid refresh token — singletonRefresh already
          // cleared auth storage. Redirect to login.
          if (!isPublicPath(window.location.pathname)) {
            sessionStorage.setItem('redirectAfterLogin', window.location.pathname + window.location.search);
            window.location.href = '/login';
          }
          throw new ApiError(401, 'Session expired');
        }

        if (isBackendUnavailable) {
          // 502/503/504/network — backend temporarily unavailable.
          // Do NOT redirect to login. Just throw so the caller can show
          // an error message. User stays logged in (auth storage intact).
          throw new ApiError(0, msg || 'Backend temporarily unavailable');
        }

        // Unexpected error — don't redirect, just throw.
        // Redirecting on unknown errors causes spontaneous logouts.
        throw new ApiError(0, msg || 'Token refresh failed');
      }
    }

    const text = await res.text().catch(() => '');
    let message = text || `HTTP ${res.status}`;
    let extra: { remainingAttempts?: number; retryAfterSeconds?: number } | undefined;
    if (text) {
      try {
        const parsed = JSON.parse(text);
        if (parsed && typeof parsed.message === 'string') {
          message = parsed.message;
        } else if (parsed && typeof parsed.error === 'string') {
          message = parsed.error;
        }
        if (parsed && typeof parsed.remainingAttempts === 'number') {
          extra = { ...extra, remainingAttempts: parsed.remainingAttempts };
        }
        if (parsed && typeof parsed.retryAfterSeconds === 'number') {
          extra = { ...extra, retryAfterSeconds: parsed.retryAfterSeconds };
        }
      } catch {
        // text was not JSON
      }
    }
    throw new ApiError(res.status, message, extra);
  }

  const contentType = res.headers.get('content-type');
  const isJson = !!contentType && contentType.includes('application/json');
  const text = await res.text();
  if (!text) return null as T;
  if (isJson) return JSON.parse(text) as T;
  return {} as T;
  } finally {
    if (!quiet) notifyLoadingDone();
  }
}

const USER_ACTION_WINDOW_MS = 500;
let lastUserActionAt = 0;

if (typeof window !== 'undefined') {
  window.addEventListener('click', (e) => {
    const target = e.target instanceof Element ? e.target : null;
    if (!target) return;
    const el = target.closest ? (target.closest('input, textarea, [contenteditable]') || target.closest('[data-no-loading]')) : null;
    if (el) return;
    lastUserActionAt = Date.now();
  });
}

/**
 * SWR fetcher that triggers the LoadingBar overlay for requests initiated
 * shortly after a user click. Background SWR revalidations (focus, polls)
 * stay silent. Buttons with `data-no-loading` and inputs are ignored.
 *
 * Usage in SWR:
 *   useSWR(url, swrFetcher)           // ← user-click → overlay, otherwise silent
 *   useSWR(url, (u) => apiFetch(u))   // ← NOT silent, shows overlay
 *
 * Prefer swrFetcher for all SWR data fetching. Use apiFetch directly
 * only for user-initiated mutations (POST/PUT/DELETE from button clicks).
 */
export function swrFetcher<T = unknown>(url: string): Promise<T> {
  const recent = Date.now() - lastUserActionAt < USER_ACTION_WINDOW_MS;
  // Hard timeout — a request stuck on a dead connection must not keep the
  // spinner forever; the abort surfaces as an error and SWR retries it.
  const signal = typeof AbortSignal !== 'undefined' && 'timeout' in AbortSignal
    ? AbortSignal.timeout(45_000)
    : undefined;
  return apiFetch<T>(url, { silent: !recent, signal });
}
